import { useFormContext } from "react-hook-form";
import EmailVerification from "../components/EmailVerification";
import PhoneVerification from "../components/PhoneVerification";
import { useEffect } from "react";
import { toast } from "react-toastify";
import DateInput from "react-calendar-ui-kit";

export default function PersonalInfoForm({ isEditing, role }: any) {
  const { register, setValue, watch, formState, trigger } = useFormContext();
  const { errors }: any = formState;

  const pinCode = watch("pinCode");
  useEffect(() => {
    const fetchPin = async () => {
      if (pinCode?.length !== 6) return;
      try {
        const res = await fetch(
          `https://api.postalpincode.in/pincode/${pinCode}`,
        );
        const data = await res.json();
        const info = data?.[0];

        if (info?.Status === "Success") {
          const list = info.PostOffice.map((x: any) => x.Name);

          setValue("stateName", info.PostOffice[0].State);
          setValue("city", info.PostOffice[0].District);
          setValue("area", list[0]);
        }
      } catch (error) {
        console.error("Pincode fetch error:", error);
        toast.error("Failed to fetch pincode info");
      }
    };

    fetchPin();
  }, [pinCode]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* First Name */}
      <div>
        <label htmlFor="firstName" className="text-sm font-medium">
          First Name *
        </label>
        <input
          id="firstName"
          className="input"
          placeholder="Enter first name"
          disabled={!isEditing}
          {...register("firstName")}
          onChange={(e) => {
            setValue("firstName", e.target.value);
            trigger("firstName");
          }}
        />

        {errors.firstName && (
          <p className="text-red-600 text-sm">{errors.firstName.message}</p>
        )}
      </div>

      {/* Last Name */}
      <div>
        <label htmlFor="lastName" className="text-sm font-medium">
          Last Name *
        </label>
        <input
          id="lastName"
          type="text"
          {...register("lastName")}
          className="input"
          placeholder="Enter last name"
          onChange={(e) => {
            setValue("lastName", e.target.value);
            trigger("lastName");
          }}
          disabled={!isEditing}
        />
        {errors.lastName && (
          <p className="text-red-600 text-sm">{errors.lastName.message}</p>
        )}
      </div>

      {/* Gender */}
      <div>
        <label htmlFor="gender" className="text-sm font-medium">
          Gender *
        </label>
        <select
          id="gender"
          {...register("gender")}
          disabled={!isEditing}
          className="input"
          onChange={(e) => {
            setValue("gender", e.target.value);
            trigger("gender");
          }}
        >
          <option value="">Select gender</option>
          <option>Male</option>
          <option>Female</option>
          <option>Other</option>
        </select>
        {errors.gender && (
          <p className="text-red-600 text-sm">{errors.gender.message}</p>
        )}
      </div>

      {/* DOB */}
      <div>
        <label htmlFor="dob" className="text-sm font-medium">
          Date of Birth *
        </label>
        <DateInput
          value={watch("dob") || ""}
          onChange={(v) => {
            setValue("dob", v);
            trigger("dob");
          }}
          disabled={!isEditing}
          theme={{
            selectWidth: "140px",
            yearButtonWidth: "120px",
          }}
        />
        {errors.dob && (
          <p className="text-red-600 text-sm">{errors.dob.message}</p>
        )}
      </div>
      {role === "Advocate" && (
        <div>
          <label htmlFor="enrollment" className="text-sm font-medium">
            Enrollment *
          </label>
          <input
            id="enrollment"
            className="input font-light text-sm "
            placeholder="Enter enrollment name"
            disabled={true}
            {...register("enrollment")}
            onChange={(e) => {
              setValue("enrollment", e.target.value);
              trigger("enrollment");
            }}
          />

          {errors.firstName && (
            <p className="text-red-600 text-sm">{errors.firstName.message}</p>
          )}
        </div>
      )}
      <EmailVerification isEditing={isEditing} />
      <div>
        <PhoneVerification isEditing={isEditing} />
      </div>
      {/* Address Line 1 */}
      <div>
        <label htmlFor="addressLine1">Address Line 1 *</label>
        <input
          id="addressLine1"
          className="input"
          disabled={!isEditing}
          maxLength={100}
          {...register("addressLine1", {
            onChange: (e) => {
              const cleaned = e.target.value
                .replace(/[^a-zA-Z0-9\s,.\-/#]/g, "")
                .slice(0, 100);
              e.target.value = cleaned;
              setValue("addressLine1", cleaned, {
                shouldValidate: true,
                shouldTouch: true,
              });
            },
          })}
          onKeyDown={(e) => {
            const val = watch("addressLine1") || "";
            if (
              val.length >= 100 &&
              ![
                "Backspace",
                "Delete",
                "ArrowLeft",
                "ArrowRight",
                "Tab",
              ].includes(e.key)
            ) {
              e.preventDefault();
            }
          }}
        />
        <p className={`text-xs text-left ${(watch("addressLine1") || "").length === 100
          ? "text-red-500 font-medium"
          : "text-gray-400"
          }`}>
          {(watch("addressLine1") || "").length === 100 && "100 characters limit exceeded."}
        </p>
        {errors.addressLine1 && (
          <p className="text-red-600 text-sm">{errors.addressLine1.message}</p>
        )}
      </div>

      {/* Address Line 2 — same pattern */}
      <div>
        <label htmlFor="addressLine2">Address Line 2 *</label>
        <input
          id="addressLine2"
          {...register("addressLine2")}
          className="input"
          disabled={!isEditing}
          maxLength={100}
          {...register("addressLine2", {
            onChange: (e) => {
              const cleaned = e.target.value
                .replace(/[^a-zA-Z0-9\s,.\-/#]/g, "")
                .slice(0, 100);
              e.target.value = cleaned;
              setValue("addressLine2", cleaned, {
                shouldValidate: true,
                shouldTouch: true,
              }); // ← add shouldTouch
            },
          })}
          onKeyDown={(e) => {
            const val = watch("addressLine2") || "";
            if (
              val.length >= 100 &&
              ![
                "Backspace",
                "Delete",
                "ArrowLeft",
                "ArrowRight",
                "Tab",
              ].includes(e.key)
            ) {
              e.preventDefault();
            }
          }}
        />
        <p className={`text-xs text-left ${(watch("addressLine2") || "").length === 100
          ? "text-red-500 font-medium"
          : "text-gray-400"
          }`}>
          {(watch("addressLine2") || "").length === 100 && "100 characters limit exceeded."}
        </p>
        {errors.addressLine2 && (
          <p className="text-red-600 text-sm">{errors.addressLine2.message}</p>
        )}
      </div>
      <div>
        <label htmlFor="pinCode">Pin Code *</label>
        <input
          id="pinCode"
          {...register("pinCode")}
          maxLength={6}
          className="input"
          disabled={!isEditing}
          onKeyDown={(e) => {
            if (
              !/^\d$/.test(e.key) &&
              ![
                "Backspace",
                "Delete",
                "ArrowLeft",
                "ArrowRight",
                "Tab",
              ].includes(e.key)
            ) {
              e.preventDefault();
            }
          }}
          onChange={(e) => {
            const digitsOnly = e.target.value.replace(/\D/g, "");
            setValue("pinCode", digitsOnly);
            trigger("pinCode");
          }}
        />
        {errors.pinCode && (
          <p className="text-red-600 text-sm">{errors.pinCode.message}</p>
        )}
      </div>
      <div>
        <label htmlFor="stateName">State</label>
        <input
          id="stateName"
          {...register("stateName")}
          className="input"
          disabled={!isEditing}
        />
      </div>
      <div>
        <label htmlFor="city">City</label>
        <input
          id="city"
          {...register("city")}
          className="input"
          disabled={!isEditing}
        />
      </div>
      <div>
        <label htmlFor="area">Area</label>
        <input
          id="area"
          {...register("area")}
          className="input"
          disabled={!isEditing}
        />
      </div>
    </div>
  );
}
